/**
 * StatusDot — the status contract (design.md §2 hard rule):
 * "Status = dot + colored text, never a filled area." A row status like
 * "Blockiert" is a small colored dot + colored text — NOT a filled pill.
 *
 * Dots keep their fixed base color in BOTH modes (--status-dot-*, design.md §9);
 * only the label text shifts with the theme (--status-*). Background stays
 * transparent, always.
 *
 * Tones: `danger` / `warning` / `success` / `info` / `neutral` are the contract
 * names; the pre-U1 names `alert` / `decide` / `ok` remain accepted aliases.
 */
import { cn } from '../../lib/cn';

const tones = {
  danger: { dot: 'bg-status-dot-danger', text: 'text-status-danger' },
  warning: { dot: 'bg-status-dot-warning', text: 'text-status-warning' },
  success: { dot: 'bg-status-dot-success', text: 'text-status-success' },
  info: { dot: 'bg-status-dot-info', text: 'text-status-info' },
  neutral: { dot: 'bg-muted-foreground/50', text: 'text-muted-foreground' },
  /* Legacy (pre-U1) aliases — same styles as the contract tones above. */
  alert: { dot: 'bg-status-dot-danger', text: 'text-status-danger' },
  decide: { dot: 'bg-status-dot-warning', text: 'text-status-warning' },
  ok: { dot: 'bg-status-dot-success', text: 'text-status-success' },
} as const;

export type StatusTone = keyof typeof tones;

export function StatusDot({ tone, label }: { tone: StatusTone; label: string }) {
  const t = tones[tone];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('size-1.5 shrink-0 rounded-full', t.dot)} />
      <span className={t.text}>{label}</span>
    </span>
  );
}
